import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    createReceipt,
    fetchReceipts,
    shareReceiptOnWhatsapp,
    fetchSavedServices,
    syncSavedServices,
} from "../../../redux/Thunk/Receipt/ReceiptThunks";
import { searchPatientsThunk } from "../../../redux/Thunk/Appointment/AppointmentThunks";
import { setPatientSearchResults } from "../../../redux/Reducer/Appointment/AppointmentSlice";
import { clearLastCreatedReceipt, setReceiptLoading } from "../../../redux/Reducer/Receipt/ReceiptSlice";
import { receiptPaymentModes } from "../../../utils/constants";
import Button from "../../common/Button";
import Download from "../../common/icons/Download";
import PlusCircle from "../../common/icons/PlusCircle";
import Delete from "../../common/icons/Delete";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";
import CustomSelect from "../../common/CustomSelect";

// Clean Outlined Icons
const CreateIcon = () => (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
);

const ServicesIcon = () => (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);

const HistoryIcon = () => (
    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
);

const UserIcon = () => (
    <svg className="w-4 h-4 text-gray-700 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const PhoneIcon = () => (
    <svg className="w-3.5 h-3.5 text-gray-400 shrink-0 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
);

const PrintIcon = () => (
    <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
    </svg>
);

const WhatsAppIcon = () => (
    <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
);

const ReceiptMaker = ({ values, setFieldValue, is_pin_b, rcptServicesAccess, rcptListAccess, defaultExpanded = false, hideCollapse = false }) => {
    const dispatch = useDispatch();
    const [isExpanded, setIsExpanded] = useState(defaultExpanded || false);
    const [activeTab, setActiveTab] = useState("create");
    const [serviceRows, setServiceRows] = useState([{ service: "", amount: "" }]);
    const [isCreated, setIsCreated] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [ageMode, setAgeMode] = useState("AGE");
    const [locationMode, setLocationMode] = useState("CITY");

    // Standalone / Local Patient Details State
    const [localPatient, setLocalPatient] = useState({
        phoneNumber: "",
        name: "",
        surname: "",
        age: "",
        dateofbirth: "",
        gender: "Male",
        city: "",
        pincode: "",
        PID: "",
    });
    const [showPatientSearchDropdown, setShowPatientSearchDropdown] = useState(false);

    const patientSearchResults = useSelector(
        (state) => state.appointment?.patientSearchResults || []
    );

    // Select Services dropdown state
    const [selectedSavedIds, setSelectedSavedIds] = useState([]);
    const [showServiceDropdown, setShowServiceDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Payment Mode state
    const [paymentMode, setPaymentMode] = useState("UPI");
    const paymentDropdownRef = useRef(null);

    // Services tab state
    const [savedServiceRows, setSavedServiceRows] = useState([]);

    const doctorDetails = useSelector(
        (state) => state?.doctor?.doctorDetails?.doctor
    );

    const receiptState = useSelector((state) => state?.receipt);
    const { receipts, lastCreatedReceipt, loading, savedServices } =
        receiptState || {};

    const DID = doctorDetails?.DID;
    const doctorName = `${doctorDetails?.personalInfo?.name || ""} ${doctorDetails?.personalInfo?.surname || ""
        }`.trim();

    // Determine effective patient object
    const effectivePatient = (values?.patient?.phoneNumber && values?.patient?.name)
        ? values.patient
        : localPatient;

    const isPatientFilled = Boolean(
        effectivePatient?.phoneNumber?.length >= 10 &&
        effectivePatient?.name?.trim()?.length > 0 &&
        (effectivePatient?.dateofbirth || String(effectivePatient?.age)?.trim()?.length > 0) &&
        effectivePatient?.gender &&
        (String(effectivePatient?.pincode)?.length === 6 || effectivePatient?.city?.trim()?.length > 0)
    );

    // Sync prefilled values into localPatient if values changes
    useEffect(() => {
        if (values?.patient?.phoneNumber) {
            setLocalPatient({
                phoneNumber: values.patient.phoneNumber || "",
                name: values.patient.name || "",
                surname: values.patient.surname || "",
                age: values.patient.age || "",
                gender: values.patient.gender || "Male",
                city: values.patient.city || "",
                pincode: values.patient.pincode || "",
                PID: values.patient.PID || "",
            });
        }
    }, [values?.patient]);

    // Build combined rows for total + create receipt
    const selectedServiceRows = savedServices
        ? savedServices.filter((s) => selectedSavedIds.includes(s.id))
        : [];

    // Calculate total amount (selected saved + manual rows)
    const totalAmount =
        selectedServiceRows.reduce((sum, s) => sum + parseFloat(s.amount || 0), 0) +
        serviceRows.reduce((sum, row) => {
            const amt = parseFloat(row.amount);
            return sum + (isNaN(amt) ? 0 : amt);
        }, 0);

    const canGenerateReceipt = isPatientFilled && totalAmount > 0 && Boolean(paymentMode);

    // Ensure activeTab is always visible
    useEffect(() => {
        const allowedTabs = [
            { key: "create", visible: true },
            { key: "services", visible: is_pin_b || rcptServicesAccess !== false },
            { key: "list", visible: is_pin_b || rcptListAccess !== false }
        ].filter(t => t.visible).map(t => t.key);

        if (!allowedTabs.includes(activeTab) && allowedTabs.length > 0) {
            setActiveTab(allowedTabs[0]);
        }
    }, [is_pin_b, rcptServicesAccess, rcptListAccess, activeTab]);

    // Fetch receipts on tab switch or page change
    useEffect(() => {
        if (activeTab === "list" && DID) {
            dispatch(
                fetchReceipts({ DID, page: currentPage, limit: 5, search: searchTerm })
            );
        }
    }, [activeTab, currentPage, DID, dispatch, searchTerm]);

    // Ensure loading state is reset on mount
    useEffect(() => {
        dispatch(setReceiptLoading(false));
    }, [dispatch]);

    // Fetch saved services on mount and when switching to relevant tabs
    useEffect(() => {
        if (DID && (activeTab === "create" || activeTab === "services")) {
            dispatch(fetchSavedServices({ DID }));
        }
    }, [activeTab, DID, dispatch]);

    // Sync savedServiceRows from Redux savedServices
    useEffect(() => {
        if (activeTab === "services" && savedServices) {
            const rows = savedServices.map((s) => ({
                id: s.id,
                serviceName: s.serviceName,
                amount: String(s.amount),
            }));
            setSavedServiceRows(rows.length > 0 ? rows : [{ serviceName: "", amount: "" }]);
        }
    }, [activeTab, savedServices]);

    // Close payment mode custom dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(event.target)) {
                const el = document.getElementById("custom-payment-menu");
                if (el) el.style.display = 'none';
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Debounced search
    const debouncedSearch = useMemo(
        () =>
            debounce((term) => {
                if (DID) {
                    setCurrentPage(1);
                    dispatch(fetchReceipts({ DID, page: 1, limit: 5, search: term }));
                }
            }, 400),
        [DID, dispatch]
    );

    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        debouncedSearch(val);
    };

    // Patient Quick Search Handler for localPatient
    const handlePatientSearch = (query) => {
        if (query.trim().length > 1) {
            dispatch(searchPatientsThunk(query));
            setShowPatientSearchDropdown(true);
        } else {
            dispatch(setPatientSearchResults([]));
            setShowPatientSearchDropdown(false);
        }
    };

    const calculateAgeFromDob = (dobString) => {
        if (!dobString) return "";
        const dob = new Date(dobString);
        if (isNaN(dob.getTime())) return "";
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
        }
        return age >= 0 ? String(age) : "";
    };

    const handleSelectSearchedPatient = (patient) => {
        const computedAge = patient.age || calculateAgeFromDob(patient.dateofbirth) || "";
        const updated = {
            phoneNumber: patient.phoneNumber || localPatient.phoneNumber,
            name: patient.name || "",
            surname: patient.surname || "",
            age: computedAge,
            dateofbirth: patient.dateofbirth || "",
            gender: patient.gender || "Male",
            city: patient.city || "",
            pincode: patient.pincode || "",
            PID: patient.PID || "",
        };
        if (patient.dateofbirth && !patient.age) {
            setAgeMode("DOB");
        } else {
            setAgeMode("AGE");
        }
        if (patient.pincode && !patient.city) {
            setLocationMode("PINCODE");
        } else {
            setLocationMode("CITY");
        }
        setLocalPatient(updated);
        if (setFieldValue) {
            setFieldValue("patient.phoneNumber", updated.phoneNumber);
            setFieldValue("patient.name", updated.name);
            setFieldValue("patient.surname", updated.surname);
            setFieldValue("patient.age", updated.age);
            setFieldValue("patient.dateofbirth", updated.dateofbirth);
            setFieldValue("patient.gender", updated.gender);
            setFieldValue("patient.city", updated.city);
            setFieldValue("patient.pincode", updated.pincode);
            setFieldValue("patient.PID", updated.PID);
        }
        setShowPatientSearchDropdown(false);
    };

    // Service row handlers (Create Receipt manual rows)
    const handleServiceChange = (index, field, value) => {
        const updated = [...serviceRows];
        if (field === "amount" && !/^\d*\.?\d*$/.test(value)) return;
        updated[index] = { ...updated[index], [field]: value };
        setServiceRows(updated);
    };

    const handleAddRow = () => {
        setServiceRows([...serviceRows, { service: "", amount: "" }]);
    };

    const handleDeleteRow = (index) => {
        setServiceRows(serviceRows.filter((_, i) => i !== index));
    };

    const handleToggleSavedService = (serviceId) => {
        setSelectedSavedIds((prev) =>
            prev.includes(serviceId)
                ? prev.filter((id) => id !== serviceId)
                : [...prev, serviceId]
        );
    };

    const handleRemoveSelectedService = (serviceId) => {
        setSelectedSavedIds((prev) => prev.filter((id) => id !== serviceId));
    };

    // Services Tab Handlers
    const handleSavedServiceChange = (index, field, value) => {
        const updated = [...savedServiceRows];
        if (field === "amount" && !/^\d*\.?\d*$/.test(value)) return;
        updated[index] = { ...updated[index], [field]: value };
        setSavedServiceRows(updated);
    };

    const handleAddSavedRow = () => {
        setSavedServiceRows([...savedServiceRows, { serviceName: "", amount: "" }]);
    };

    const handleDeleteSavedRow = (index) => {
        setSavedServiceRows(savedServiceRows.filter((_, i) => i !== index));
    };

    const handleSaveServicesTab = async () => {
        const validRows = savedServiceRows.filter(
            (r) => r.serviceName.trim() && r.amount && parseFloat(r.amount) > 0
        );
        if (validRows.length === 0) {
            toast.error("Please enter at least one valid service with name and amount");
            return;
        }

        const servicesToSave = validRows.map((r, i) => ({
            id: r.id || `srv_${Date.now()}_${i}`,
            serviceName: r.serviceName.trim(),
            amount: parseFloat(r.amount),
        }));

        await dispatch(syncSavedServices({ DID, services: servicesToSave }));
    };

    const getMissingFields = () => {
        const missing = [];
        if (!effectivePatient?.phoneNumber || effectivePatient.phoneNumber.length < 10)
            missing.push("Valid 10-digit Phone Number");
        if (!effectivePatient?.name || !effectivePatient.name.trim()) missing.push("Patient Name");
        if (!effectivePatient?.dateofbirth && (!effectivePatient?.age || isNaN(effectivePatient.age)))
            missing.push("Age or Date of Birth");
        if (!effectivePatient?.gender) missing.push("Gender");
        if (!effectivePatient?.city && (!effectivePatient?.pincode || effectivePatient.pincode.length !== 6))
            missing.push("City or Pincode");

        const hasSelectedSaved = selectedSavedIds.length > 0;
        const hasManualRows = serviceRows.some((r) => r.service.trim() && r.amount);
        if (!hasSelectedSaved && !hasManualRows) {
            missing.push("At least one service with valid amount");
        }
        if (!paymentMode) missing.push("Payment Mode");
        return missing;
    };

    const handleCreateReceipt = async () => {
        const missing = getMissingFields();
        if (missing.length > 0) {
            toast.error(`Please fill: ${missing.join(", ")}`);
            return;
        }

        const allServices = [
            ...selectedServiceRows.map((s) => ({
                service: s.serviceName,
                amount: parseFloat(s.amount),
            })),
            ...serviceRows
                .filter((row) => row.service.trim() && row.amount && parseFloat(row.amount) > 0)
                .map((row) => ({
                    service: row.service.trim(),
                    amount: parseFloat(row.amount),
                })),
        ];

        const receiptData = {
            DID,
            patientPhoneNumber: effectivePatient?.phoneNumber,
            patientName: effectivePatient?.name,
            patientSurname: effectivePatient?.surname || "",
            patientGender: effectivePatient?.gender || "",
            patientDob: effectivePatient?.dateofbirth || null,
            patientAge: effectivePatient?.age || null,
            patientPincode: effectivePatient?.pincode || "",
            patientCity: effectivePatient?.city || "",
            services: allServices,
            paymentMode,
        };

        const response = await dispatch(createReceipt(receiptData)).unwrap();
        if (response?.status === 201) {
            setIsCreated(true);
            if (response.data?.receipt?.PID) {
                if (setFieldValue) {
                    setFieldValue("patient.PID", response.data.receipt.PID);
                }
                setLocalPatient((prev) => ({ ...prev, PID: response.data.receipt.PID }));
            }
        }
    };

    const handleClose = () => {
        setIsCreated(false);
        setServiceRows([{ service: "", amount: "" }]);
        setSelectedSavedIds([]);
        setPaymentMode("UPI");
        dispatch(clearLastCreatedReceipt());
    };

    const handleDownload = async (pdfUrl, patientName) => {
        try {
            const res = await fetch(pdfUrl);
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `Receipt_${patientName.replace(/\s+/g, "_")}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
            toast.error("Failed to download receipt");
        }
    };

    const handleShare = async (receipt) => {
        const shareData = {
            RID: receipt.RID,
            patientPhoneNumber: receipt.patientPhoneNumber,
            patientName: `${receipt.patientName} ${receipt.patientSurname || ""}`.trim(),
            doctorName: `${doctorName}`,
            pdfUrl: receipt.pdfUrl,
        };
        await dispatch(shareReceiptOnWhatsapp(shareData));
    };

    const handlePrint = async (pdfUrl) => {
        try {
            const res = await fetch(pdfUrl);
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const printWindow = window.open(blobUrl);
            if (printWindow) {
                printWindow.addEventListener("load", () => {
                    printWindow.print();
                });
            }
        } catch (error) {
            toast.error("Failed to print receipt");
        }
    };

    if (!isExpanded && !defaultExpanded) {
        return (
            <div className="w-full mt-3 mb-6 md:mb-10 bg-white border border-gray-200 rounded-2xl p-3.5 shadow-xs flex items-center justify-between transition-all">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-primary-50 border border-primary-100 flex items-center justify-center shrink-0 text-primary-600">
                        <CreateIcon />
                    </div>
                    <div>
                        <h3 className="text-xs font-bold text-gray-800 leading-tight">
                            Receipt Maker
                        </h3>
                        <p className="text-[11px] text-gray-400 font-medium">
                            Create patient billing receipts on demand
                        </p>
                    </div>
                </div>
                <Button
                    type="button"
                    onClick={() => setIsExpanded(true)}
                    className="flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold py-2 px-3.5 rounded-xl shadow-xs transition-all shrink-0 cursor-pointer"
                >
                    <PlusCircle width={16} height={16} fill="currentColor" />
                    <span>Create Receipt</span>
                </Button>
            </div>
        );
    }

    return (
        <div className="w-full mt-2 mb-6 md:mb-10 bg-white rounded-2xl border border-gray-100 shadow-xs p-4 sm:p-5 space-y-5">
            {/* Minimal Inner Segmented Tab Bar with Outlined Icons */}
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-3">
                <div className="bg-gray-100/80 p-1.5 rounded-2xl border border-gray-200/60 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
                    {[
                        { key: "create", label: "Create Receipt", icon: <CreateIcon /> },
                        { key: "services", label: "Saved Services", icon: <ServicesIcon /> },
                        { key: "list", label: "Receipt Logs", icon: <HistoryIcon /> },
                    ].filter(tab => {
                        if (is_pin_b) return true;
                        if (tab.key === "services" && !rcptServicesAccess) return false;
                        if (tab.key === "list" && !rcptListAccess) return false;
                        return true;
                    }).map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            className={`px-4 py-2 rounded-xl text-xs transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${activeTab === tab.key
                                    ? "bg-white text-gray-900 font-bold shadow-xs border border-gray-200/80"
                                    : "text-gray-600 hover:text-gray-900 font-medium hover:bg-white/50"
                                }`}
                        >
                            {tab.icon}
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                {!hideCollapse && !defaultExpanded && (
                    <button
                        type="button"
                        onClick={() => setIsExpanded(false)}
                        className="text-xs font-bold text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-xl transition-all shrink-0 cursor-pointer"
                    >
                        Collapse
                    </button>
                )}
            </div>

            {/* TAB 1: CREATE RECEIPT */}
            {activeTab === "create" && (
                <div className="space-y-5">
                    {/* Patient Details Section */}
                    {(!values?.patient?.phoneNumber || !isPatientFilled) && (
                        <div className="p-4 sm:p-5 rounded-2xl bg-gray-50/80 border border-gray-200/70 space-y-3.5 shadow-2xs">
                            <div className="flex items-center justify-between">
                                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                                    <UserIcon />
                                    <span>Patient Details for Receipt</span>
                                </h4>
                                {isPatientFilled && (
                                    <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                                        ✓ Details Validated
                                    </span>
                                )}
                            </div>

                            {/* Patient Search Input */}
                            <div className="relative">
                                <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                                    Phone Number / Quick Patient Search <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Type Phone number or Name to search existing patient..."
                                    value={localPatient.phoneNumber}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setLocalPatient((prev) => ({ ...prev, phoneNumber: val }));
                                        handlePatientSearch(val);
                                    }}
                                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold bg-white outline-none focus:ring-2 focus:ring-primary-500 shadow-2xs"
                                />

                                {showPatientSearchDropdown && patientSearchResults.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-48 overflow-y-auto divide-y divide-gray-100">
                                        {patientSearchResults.map((p) => (
                                            <div
                                                key={p.PID}
                                                onMouseDown={() => handleSelectSearchedPatient(p)}
                                                className="p-2.5 hover:bg-primary-50 cursor-pointer flex items-center justify-between text-xs"
                                            >
                                                <div>
                                                    <span className="font-bold text-gray-900">{p.name} {p.surname || ""}</span>
                                                    <span className="text-gray-500 ml-2">({p.phoneNumber})</span>
                                                </div>
                                                <span className="text-[10px] font-bold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                                                    PID: {p.PID}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Patient Information Inputs */}
                            <div className="space-y-3 text-xs">
                                {/* Row 1: First Name - Last Name - Gender */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div>
                                        <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                                            First Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="First Name"
                                            value={localPatient.name}
                                            onChange={(e) => setLocalPatient({ ...localPatient, name: e.target.value })}
                                            className="w-full px-3.5 py-2 rounded-xl border border-gray-200 font-semibold bg-white outline-none shadow-2xs"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                                            Surname
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Surname"
                                            value={localPatient.surname}
                                            onChange={(e) => setLocalPatient({ ...localPatient, surname: e.target.value })}
                                            className="w-full px-3.5 py-2 rounded-xl border border-gray-200 font-semibold bg-white outline-none shadow-2xs"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                                            Gender <span className="text-red-500">*</span>
                                        </label>
                                        <CustomSelect
                                            value={localPatient.gender}
                                            onChange={(val) => setLocalPatient({ ...localPatient, gender: val })}
                                            options={[
                                                { value: "Male", label: "Male" },
                                                { value: "Female", label: "Female" },
                                                { value: "Other", label: "Other" },
                                            ]}
                                        />
                                    </div>
                                </div>

                                {/* Row 2: Age/DOB - City/Pincode */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <label className="block text-[11px] font-bold text-gray-600 uppercase shrink-0">
                                                {ageMode === "AGE" ? "Age (Yrs)" : "Date of Birth"} <span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex-1 max-w-[130px] flex items-center p-0.5 bg-gray-200/70 rounded-md text-[9px] font-bold ml-auto">
                                                <button
                                                    type="button"
                                                    onClick={() => setAgeMode("AGE")}
                                                    className={`flex-1 py-0.5 text-center rounded transition-all cursor-pointer ${ageMode === "AGE" ? "bg-white text-gray-900 shadow-2xs font-extrabold" : "text-gray-500 hover:text-gray-900"}`}
                                                >
                                                    Age
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setAgeMode("DOB")}
                                                    className={`flex-1 py-0.5 text-center rounded transition-all cursor-pointer ${ageMode === "DOB" ? "bg-white text-gray-900 shadow-2xs font-extrabold" : "text-gray-500 hover:text-gray-900"}`}
                                                >
                                                    DOB
                                                </button>
                                            </div>
                                        </div>
                                        {ageMode === "AGE" ? (
                                            <input
                                                type="number"
                                                placeholder="Years"
                                                value={localPatient.age}
                                                onChange={(e) => setLocalPatient({ ...localPatient, age: e.target.value })}
                                                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 font-semibold bg-white outline-none shadow-2xs"
                                            />
                                        ) : (
                                            <input
                                                type="date"
                                                value={localPatient.dateofbirth || ""}
                                                onChange={(e) => {
                                                    const dob = e.target.value;
                                                    const calculatedAge = calculateAgeFromDob(dob);
                                                    setLocalPatient({ ...localPatient, dateofbirth: dob, age: calculatedAge });
                                                }}
                                                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 font-semibold bg-white outline-none shadow-2xs"
                                            />
                                        )}
                                    </div>

                                    <div>
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <label className="block text-[11px] font-bold text-gray-600 uppercase shrink-0">
                                                {locationMode === "CITY" ? "City" : "Pincode"}
                                            </label>
                                            <div className="flex-1 max-w-[130px] flex items-center p-0.5 bg-gray-200/70 rounded-md text-[9px] font-bold ml-auto">
                                                <button
                                                    type="button"
                                                    onClick={() => setLocationMode("CITY")}
                                                    className={`flex-1 py-0.5 text-center rounded transition-all cursor-pointer ${locationMode === "CITY" ? "bg-white text-gray-900 shadow-2xs font-extrabold" : "text-gray-500 hover:text-gray-900"}`}
                                                >
                                                    City
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setLocationMode("PINCODE")}
                                                    className={`flex-1 py-0.5 text-center rounded transition-all cursor-pointer ${locationMode === "PINCODE" ? "bg-white text-gray-900 shadow-2xs font-extrabold" : "text-gray-500 hover:text-gray-900"}`}
                                                >
                                                    Pin
                                                </button>
                                            </div>
                                        </div>
                                        {locationMode === "CITY" ? (
                                            <input
                                                type="text"
                                                placeholder="City Name"
                                                value={localPatient.city}
                                                onChange={(e) => setLocalPatient({ ...localPatient, city: e.target.value })}
                                                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 font-semibold bg-white outline-none shadow-2xs"
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                placeholder="6-digit Pincode"
                                                value={localPatient.pincode}
                                                onChange={(e) => setLocalPatient({ ...localPatient, pincode: e.target.value })}
                                                className="w-full px-3.5 py-2 rounded-xl border border-gray-200 font-semibold bg-white outline-none shadow-2xs"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Receipt Services & Payment Section */}
                    {isPatientFilled ? (
                        <div className="space-y-4">
                            {/* Payment & Services Selection Row */}
                            <div className="flex flex-col sm:flex-row gap-3">
                                {/* Payment Mode Dropdown */}
                                <div className="w-full sm:max-w-[200px]" ref={paymentDropdownRef}>
                                    <div className="relative">
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => {
                                                if (isCreated) return;
                                                const currentTarget = e.currentTarget;
                                                const dropdown = currentTarget.nextElementSibling;
                                                if (dropdown) {
                                                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                                                }
                                            }}
                                            className={`w-full h-[42px] px-3.5 py-2 border rounded-xl bg-gray-50/90 font-bold text-xs outline-none cursor-pointer transition-all flex items-center shadow-2xs ${paymentMode
                                                ? "border-gray-200 text-gray-800"
                                                : "border-gray-200 text-gray-500"
                                                } ${isCreated ? "bg-gray-100 text-gray-700 border-gray-200 cursor-default" : ""}`}
                                        >
                                            <span className="truncate flex-1">{paymentMode || "Select Payment Mode"}</span>
                                            {!isCreated && (
                                                <span className="text-[10px] text-primary-600 font-bold whitespace-nowrap ml-1">(Change)</span>
                                            )}
                                        </div>
                                        <div className="payment-mode-dropdown absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl max-h-[200px] overflow-y-auto z-10 hidden shadow-xl divide-y divide-gray-100">
                                            {receiptPaymentModes.map((option) => (
                                                <div
                                                    key={option.value}
                                                    onMouseDown={() => {
                                                        setPaymentMode(option.value);
                                                        if (paymentDropdownRef.current) {
                                                            const el = paymentDropdownRef.current.querySelector('.payment-mode-dropdown');
                                                            if (el) el.style.display = 'none';
                                                        }
                                                    }}
                                                    className="px-3.5 py-2.5 text-xs font-semibold hover:bg-primary-50 cursor-pointer text-gray-800"
                                                >
                                                    {option.label}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Select Saved Services Dropdown */}
                                <div className="w-full sm:flex-1 relative" ref={dropdownRef}>
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => !isCreated && setShowServiceDropdown(!showServiceDropdown)}
                                        className={`w-full h-[42px] px-3.5 py-2 border rounded-xl bg-white text-xs font-semibold outline-none cursor-pointer transition-all flex items-center justify-between shadow-2xs ${showServiceDropdown
                                            ? "border-primary-500 ring-2 ring-primary-500/20"
                                            : "border-gray-200 hover:border-gray-300"
                                            } ${isCreated ? "bg-gray-50 border-gray-200 cursor-default" : ""}`}
                                    >
                                        <span className="truncate text-gray-600">
                                            {selectedSavedIds.length > 0
                                                ? `✓ ${selectedSavedIds.length} service(s) selected`
                                                : "Select Saved Services..."}
                                        </span>
                                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${showServiceDropdown ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>

                                    {showServiceDropdown && !isCreated && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-[220px] overflow-y-auto z-20 p-1">
                                            {!savedServices || savedServices.length === 0 ? (
                                                <div className="px-3 py-2.5 text-xs text-gray-400 text-center font-medium">
                                                    No saved services found. Add them in the "Saved Services" tab.
                                                </div>
                                            ) : (
                                                savedServices.map((s) => {
                                                    const isChecked = selectedSavedIds.includes(s.id);
                                                    return (
                                                        <div
                                                            key={s.id}
                                                            onClick={() => handleToggleSavedService(s.id)}
                                                            className={`flex items-center justify-between px-3.5 py-2.5 rounded-lg text-xs cursor-pointer transition-all ${isChecked ? "bg-primary-50 text-primary-700 font-bold" : "hover:bg-gray-50 text-gray-800"
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isChecked}
                                                                    onChange={() => { }}
                                                                    className="w-3.5 h-3.5 text-primary-600 rounded border-gray-300 cursor-pointer"
                                                                />
                                                                <span className="truncate">{s.serviceName}</span>
                                                            </div>
                                                            <span className="text-xs font-black text-gray-900 shrink-0 ml-2">₹{s.amount}</span>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Service Rows */}
                            <div className="space-y-2.5">
                                {/* Selected Saved Services Rows */}
                                {selectedServiceRows.map((s) => (
                                    <div
                                        key={s.id}
                                        className="flex items-center gap-2 bg-gray-50 border border-gray-200/80 p-2.5 rounded-2xl shadow-2xs"
                                    >
                                        <input
                                            type="text"
                                            value={s.serviceName}
                                            disabled
                                            className="flex-1 px-3.5 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-800"
                                        />
                                        <div className="w-[110px] sm:w-[130px] relative">
                                            <input
                                                type="text"
                                                value={s.amount}
                                                disabled
                                                className="w-full px-3.5 py-2 bg-white border border-gray-200 rounded-xl text-xs font-black text-gray-900 text-right pr-7"
                                            />
                                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">₹</span>
                                        </div>
                                        {!isCreated && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSelectedService(s.id)}
                                                className="p-2 text-gray-400 hover:text-red-600 rounded-xl hover:bg-red-50 transition-all shrink-0 cursor-pointer"
                                                title="Remove Service"
                                            >
                                                <Delete width={16} height={16} />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                {/* Manual Particular Rows */}
                                {serviceRows.map((row, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            placeholder="Particulars (e.g., Consultation Fee)"
                                            value={row.service}
                                            onChange={(e) => handleServiceChange(index, "service", e.target.value)}
                                            disabled={isCreated}
                                            className="flex-1 px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 shadow-2xs"
                                        />
                                        <div className="w-[110px] sm:w-[130px] relative">
                                            <input
                                                type="text"
                                                placeholder="0.00"
                                                value={row.amount}
                                                onChange={(e) => handleServiceChange(index, "amount", e.target.value)}
                                                disabled={isCreated}
                                                className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-bold text-gray-900 text-right pr-7 outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 shadow-2xs"
                                            />
                                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">₹</span>
                                        </div>
                                        {!isCreated && (
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteRow(index)}
                                                className="p-2 text-gray-400 hover:text-red-600 rounded-xl hover:bg-red-50 transition-all shrink-0 cursor-pointer"
                                                title="Delete Row"
                                            >
                                                <Delete width={16} height={16} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Add Particular Line Button */}
                            {!isCreated && (
                                <button
                                    type="button"
                                    onClick={handleAddRow}
                                    className="flex items-center gap-1.5 text-xs font-bold text-primary-600 hover:text-primary-700 hover:bg-primary-50 px-3.5 py-2 rounded-xl transition-all cursor-pointer"
                                >
                                    <PlusCircle width={14} height={14} fill="currentColor" />
                                    <span>Add Particular Line</span>
                                </button>
                            )}

                            {/* Total Amount & Generate Button */}
                            <div className="pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-gray-600">Total Amount:</span>
                                    <span className="text-xl font-black text-gray-900">₹{totalAmount.toFixed(2)}</span>
                                </div>

                                {!isCreated ? (
                                    <Button
                                        type="button"
                                        onClick={handleCreateReceipt}
                                        disabled={loading}
                                        className={`w-full sm:w-auto font-bold text-xs py-2.5 px-6 rounded-2xl transition-all cursor-pointer flex items-center justify-center gap-2 ${canGenerateReceipt
                                            ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-600/20 hover:scale-[1.02]"
                                            : "bg-primary-600 hover:bg-primary-700 text-white shadow-sm"
                                            }`}
                                    >
                                        {loading ? "Generating Receipt..." : canGenerateReceipt ? "✓ Create & Generate Receipt" : "Create & Generate Receipt"}
                                    </Button>
                                ) : (
                                    <div className="flex items-center gap-2 w-full sm:w-auto">
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="px-4 py-2 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all cursor-pointer"
                                        >
                                            New Receipt
                                        </button>
                                        {lastCreatedReceipt?.pdfUrl && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => handlePrint(lastCreatedReceipt.pdfUrl)}
                                                    className="px-3.5 py-2 text-xs font-bold text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
                                                >
                                                    <PrintIcon />
                                                    <span>Print</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleShare(lastCreatedReceipt)}
                                                    className="px-3.5 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
                                                >
                                                    <WhatsAppIcon />
                                                    <span>WhatsApp</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDownload(lastCreatedReceipt.pdfUrl, lastCreatedReceipt.patientName)}
                                                    className="px-3.5 py-2 text-xs font-bold text-white bg-primary-600 hover:bg-primary-700 rounded-xl shadow-xs transition-all cursor-pointer flex items-center gap-1.5"
                                                >
                                                    <Download width={14} height={14} fill="currentColor" />
                                                    <span>PDF</span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl text-center text-xs font-bold text-amber-800">
                            Please complete the Patient Details above to proceed with receipt generation.
                        </div>
                    )}
                </div>
            )}

            {/* TAB 2: SAVED SERVICES MANAGER */}
            {activeTab === "services" && (
                <div className="space-y-4">
                    <div>
                        <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                            Manage Standard Consultation Services & Preset Fees
                        </h4>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                            Save your commonly billed services (e.g., First Consultation, Follow-up) for 1-click selection during receipt creation.
                        </p>
                    </div>

                    <div className="space-y-2.5">
                        {savedServiceRows.map((row, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <input
                                    type="text"
                                    placeholder="Service Name (e.g., General Consultation)"
                                    value={row.serviceName}
                                    onChange={(e) => handleSavedServiceChange(index, "serviceName", e.target.value)}
                                    className="flex-1 px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-primary-500 shadow-2xs"
                                />
                                <div className="w-[110px] sm:w-[130px] relative">
                                    <input
                                        type="text"
                                        placeholder="0.00"
                                        value={row.amount}
                                        onChange={(e) => handleSavedServiceChange(index, "amount", e.target.value)}
                                        className="w-full px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-bold text-gray-900 text-right pr-7 outline-none focus:ring-2 focus:ring-primary-500 shadow-2xs"
                                    />
                                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">₹</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteSavedRow(index)}
                                    className="p-2 text-gray-400 hover:text-red-600 rounded-xl hover:bg-red-50 transition-all shrink-0 cursor-pointer"
                                    title="Delete Service"
                                >
                                    <Delete width={16} height={16} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={handleAddSavedRow}
                            className="flex items-center gap-1.5 text-xs font-bold text-primary-600 hover:text-primary-700 hover:bg-primary-50 px-3.5 py-2 rounded-xl transition-all cursor-pointer"
                        >
                            <PlusCircle width={14} height={14} fill="currentColor" />
                            <span>Add Service Line</span>
                        </button>
                        <Button
                            type="button"
                            onClick={handleSaveServicesTab}
                            className="bg-gray-900 hover:bg-black text-white font-bold text-xs py-2.5 px-6 rounded-2xl shadow-md transition-all cursor-pointer"
                        >
                            Save Services
                        </Button>
                    </div>
                </div>
            )}

            {/* TAB 3: RECEIPT HISTORY LOG */}
            {activeTab === "list" && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                        <input
                            type="text"
                            placeholder="Search receipt log by patient name or RID..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="w-full px-3.5 py-2.5 border border-gray-200 rounded-2xl text-xs font-semibold bg-white outline-none focus:ring-2 focus:ring-primary-500 shadow-2xs"
                        />
                    </div>

                    <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-2xs bg-white">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-gray-100/90 border-b border-gray-200 font-bold text-gray-700 uppercase tracking-wider">
                                <tr>
                                    <th className="p-3.5 w-[14%] text-left whitespace-nowrap">RID</th>
                                    <th className="p-3.5 w-[40%] text-left whitespace-nowrap">Patient</th>
                                    <th className="p-3.5 w-[15%] text-left whitespace-nowrap">Amount</th>
                                    <th className="p-3.5 w-[15%] text-left whitespace-nowrap">Payment</th>
                                    <th className="p-3.5 w-[16%] text-right whitespace-nowrap">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {receipts.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="p-8 text-center text-gray-400 font-bold">
                                            No receipts created yet.
                                        </td>
                                    </tr>
                                ) : (
                                    receipts.map((rcpt) => (
                                        <tr key={rcpt.RID} className="hover:bg-gray-50/80 transition-all">
                                            <td className="p-3.5 w-[14%] font-bold text-primary-700 whitespace-nowrap">{rcpt.RID}</td>
                                            <td className="p-3.5 w-[40%]">
                                                <span className="font-bold text-gray-900 block">{rcpt.patientName} {rcpt.patientSurname || ""}</span>
                                                <span className="text-[11px] font-semibold text-gray-400 flex items-center gap-1 mt-0.5">
                                                    <PhoneIcon />
                                                    {rcpt.patientPhoneNumber}
                                                </span>
                                            </td>
                                            <td className="p-3.5 w-[15%] font-black text-gray-900 whitespace-nowrap">₹{rcpt.receiptAmount}</td>
                                            <td className="p-3.5 w-[15%] font-bold text-gray-600 whitespace-nowrap">{rcpt.paymentMode}</td>
                                            <td className="p-3.5 w-[16%] text-right whitespace-nowrap">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => handlePrint(rcpt.pdfUrl)}
                                                        className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-xs font-bold flex items-center gap-1"
                                                    >
                                                        <PrintIcon />
                                                        <span>Print</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleShare(rcpt)}
                                                        className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold flex items-center gap-1"
                                                    >
                                                        <WhatsAppIcon />
                                                        <span>WhatsApp</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownload(rcpt.pdfUrl, rcpt.patientName)}
                                                        className="px-2.5 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shrink-0"
                                                    >
                                                        <Download width={13} height={13} fill="currentColor" className="shrink-0" />
                                                        <span>PDF</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReceiptMaker;
